import { STRING } from 'resources'

import {
  PROFILE_REWARDS_CATEGORIES_ORDER,
  PROFILE_REWARDS_CATEGORIES,
  PROFILE_REWARDS_QUESTION_TYPES,
  PROFILE_REWARDS_CUSTOM_QUESTION_TYPES,
} from './constants'

export const groupProfileRewardsQuestions = (profileRewardsQuestions) => {
  if (!profileRewardsQuestions) {
    return {}
  }

  const groupedProfileRewardsQuestions = profileRewardsQuestions.reduce((groups, question) => {
    if (groups[question.category]) {
      groups[question.category].push(question)
      return groups
    }
    return {
      ...groups,
      [question.category]: [question],
    }
  }, {})

  const sortedGroupedProfileRewardsQuestions = Object.entries(groupedProfileRewardsQuestions).reduce(
    (groups, [category, questions]) => {
      const sortedQuestions = questions
        .map((question) => ({
          ...question,
          choices: question.choices.sort((c1, c2) => (c1.order > c2.order ? 1 : -1)),
        }))
        .sort((q1, q2) => (q1.categoryOrder > q2.categoryOrder ? 1 : -1))

      return { ...groups, [category]: sortedQuestions }
    },
    {},
  )

  return sortedGroupedProfileRewardsQuestions
}

export const sortGroupedProfileRewardsQuestions = (groupedProfileRewardsQuestions) =>
  PROFILE_REWARDS_CATEGORIES_ORDER.map((category) => ({
    category,
    questions: groupedProfileRewardsQuestions[category],
  })).filter(({ questions }) => questions && questions.length > 0)

/**
 * Profile Rewards Question
 * @typedef {object} ProfileRewardsQuestion
 */

/**
 * Profile Rewards Carousel Item
 * @typedef {object} ProfileRewardsCarouselItem
 * @property {string} key - Key of the item
 * @property {string} isAddItem - Whether it is Add Item for Multi-Select
 * @property {string} [question] - Question Text. Provide one of both of Question and Answer.
 * @property {string} [answer] - Answer Text. Provide one of both of Question and Answer.
 * @property {string} [icon] - Icon for this item
 * @property {boolean} isRequired - Whether the item is required
 * @property {string} [onClick] - Function to call when the item is clicked
 */

const textQuestionToCardItem = (question, setEditItem) => ({
  key: question.key,
  isAddItem: false,
  question: question.label,
  answer: question.answers?.length > 0 ? question.answers[0].answerText : null,
  icon: question.icon,
  isRequired: false,
  onClick: () =>
    setEditItem({
      key: question.key,
      category: question.category,
      type: question.type,
      label: question.label,
      id: question.id,
      choices: null,
      answers: question.answers,
    }),
})

const singleSelectQuestionToCardItem = (question, setEditItem) => {
  let choice = null
  const answer = question.answers.length > 0 ? question.answers[0] : null
  if (answer) {
    choice = question.choices.find((c) => c.id === answer.selectedChoiceId)
  }

  return {
    key: question.key,
    isAddItem: false,
    question: question.label,
    answer: choice ? choice.label : null,
    icon: question.icon,
    isRequired: false,
    onClick: () =>
      setEditItem({
        key: question.key,
        category: question.category,
        type: question.type,
        label: question.label,
        id: question.id,
        choices: question.choices,
        answers: question.answers,
      }),
  }
}

const multipleSelectQuestionToCardItems = (
  question,
  setEditItem,
  isReadOnlyMode = false,
  isEmptyAttributes = false,
) => {
  const onMultipleSelectQuestionEdit = () =>
    setEditItem({
      key: question.key,
      category: question.category,
      type: question.type,
      label: question.label,
      id: question.id,
      choices: question.choices,
      answers: question.answers,
    })

  if (isReadOnlyMode && isEmptyAttributes) {
    return question.choices.map((choice) => ({
      key: choice.key,
      isAddItem: false,
      question: null,
      answer: choice.label,
      icon: choice.icon,
      isRequired: false,
      onClick: null,
    }))
  }

  return [
    ...question.choices
      .map((choice) => {
        const answer = question.answers.find((a) => a.selectedChoiceId === choice.id)
        if (!answer) {
          return null
        }

        return {
          key: choice.key,
          isAddItem: false,
          question: null,
          answer: choice.label,
          icon: choice.icon,
          isRequired: false,
          onClick: onMultipleSelectQuestionEdit,
        }
      })
      .filter(Boolean),
    ...(isReadOnlyMode
      ? []
      : [
          {
            key: `add_${question.key}`,
            isAddItem: true,
            addLabel: question.addLabel,
            onClick: onMultipleSelectQuestionEdit,
          },
        ]),
  ]
}

export const postalCodeQuestionToCardItem = (postalCodeQuestion, setEditItem) => {
  const labelText = postalCodeQuestion?.label ?? ''
  const answerText = postalCodeQuestion?.answers?.[0]?.answerText ?? null
  return {
    key: 'postalCode',
    isAddItem: false,
    question: labelText,
    answer: answerText,
    icon: 'mark-unread-mailbox',
    isRequired: true,
    onClick: () =>
      setEditItem({
        key: 'postalCode',
        category: PROFILE_REWARDS_CATEGORIES.TRAIT,
        type: PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.TEXT_POSTAL_CODE,
        label: labelText,
        value: answerText,
      }),
  }
}

export const genderQuestionToCardItem = (genderQuestion, setEditItem) => {
  const labelText = genderQuestion?.label ?? ''
  const answerKey = genderQuestion?.answers?.[0]?.answerText ?? null
  const answerText = answerKey && STRING.gender.values[answerKey]
  return {
    key: 'gender',
    isAddItem: false,
    question: labelText,
    answer: answerText,
    icon: 'gender',
    isRequired: true,
    onClick: () =>
      setEditItem({
        key: 'gender',
        category: PROFILE_REWARDS_CATEGORIES.TRAIT,
        type: PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.SINGLE_SELECT_GENDER,
        label: labelText,
        value: answerKey,
      }),
  }
}

export const dateOfBirthQuestionToCardItem = (dateOfBirthQuestion, setEditItem) => {
  const labelText = dateOfBirthQuestion?.label ?? ''
  const answerText = dateOfBirthQuestion?.answers?.[0]?.answerText ?? null
  return {
    key: 'dateOfBirth',
    isAddItem: false,
    question: labelText,
    answer: answerText,
    icon: 'cake',
    isRequired: true,
    onClick: () =>
      setEditItem({
        key: 'dateOfBirth',
        category: PROFILE_REWARDS_CATEGORIES.TRAIT,
        type: PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.DATE_DATE_OF_BIRTH,
        label: labelText,
        value: answerText,
      }),
  }
}

/**
 * Sorts surveys by `end` field in ascending order.
 * @param {ProfileRewardsQuestion[]} questions -  List of questions to compose into items.
 * @param {function} setEditItem - Used for setting up onClick to edit the item
 * @param {boolean} [isReadOnlyMode] - Wether the items should be composed as read-only mode.
 * @param {boolean} [isEmptyAttributes] - This and isReadOnlyMode decides whether
 * the Multi-Select Choices should be fully expanded.
 * @returns {ProfileRewardsCarouselItem[]} - List of Carousel Items to be used
 * to render the carousel.
 */
export const questionsToItems = (questions, setEditItem, isReadOnlyMode = false, isEmptyAttributes = false) =>
  questions.reduce((cardItems, question) => {
    switch (question.type) {
      case PROFILE_REWARDS_QUESTION_TYPES.TEXT:
        return [...cardItems, textQuestionToCardItem(question, setEditItem)]
      case PROFILE_REWARDS_QUESTION_TYPES.SINGLE_SELECT:
        return [...cardItems, singleSelectQuestionToCardItem(question, setEditItem)]
      case PROFILE_REWARDS_QUESTION_TYPES.MULTIPLE_SELECT:
        return [
          ...cardItems,
          ...multipleSelectQuestionToCardItems(question, setEditItem, isReadOnlyMode, isEmptyAttributes),
        ]
      case PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.TEXT_POSTAL_CODE:
        return [...cardItems, postalCodeQuestionToCardItem(question, setEditItem)]
      case PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.SINGLE_SELECT_GENDER:
        return [...cardItems, genderQuestionToCardItem(question, setEditItem)]
      case PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.DATE_DATE_OF_BIRTH:
        return [...cardItems, dateOfBirthQuestionToCardItem(question, setEditItem)]
      default:
        return cardItems
    }
  }, [])

export const getProfileRewardsRequiredQuestions = (region, postalCode, gender, dateOfBirth) => [
  {
    id: 'postal_code',
    key: 'postal-code',
    category: PROFILE_REWARDS_CATEGORIES.TRAIT,
    categoryOrder: -3,
    label: STRING.profileRewardsQuestions.profileInfoQuestions.postalCode[region],
    addLabel: '',
    type: PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.TEXT_POSTAL_CODE,
    icon: 'mark-unread-mailbox',
    choices: [],
    answers: postalCode
      ? [
          {
            id: 'postal-code-answer',
            questionId: 'postal_code',
            selectedChoiceId: null,
            answerText: postalCode,
            source: null,
          },
        ]
      : [],
  },
  {
    id: 'gender',
    key: 'gender',
    category: PROFILE_REWARDS_CATEGORIES.TRAIT,
    categoryOrder: -2,
    label: STRING.profileRewardsQuestions.profileInfoQuestions.gender,
    addLabel: '',
    type: PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.SINGLE_SELECT_GENDER,
    icon: 'gender',
    choices: [],
    answers: gender
      ? [
          {
            id: 'gender_answer',
            questionId: 'gender',
            selectedChoiceId: null,
            answerText: gender,
            source: null,
          },
        ]
      : [],
  },
  {
    id: 'date_of_birth',
    key: 'date-of-birth',
    category: PROFILE_REWARDS_CATEGORIES.TRAIT,
    categoryOrder: -1,
    label: STRING.profileRewardsQuestions.profileInfoQuestions.dateOfBirth,
    addLabel: '',
    type: PROFILE_REWARDS_CUSTOM_QUESTION_TYPES.DATE_DATE_OF_BIRTH,
    icon: 'cake',
    choices: [],
    answers: dateOfBirth
      ? [
          {
            id: 'date_of_birth_answer',
            questionId: 'date_of_birth',
            selectedChoiceId: null,
            answerText: dateOfBirth,
            source: null,
          },
        ]
      : [],
  },
]

export const setFullUnveilConflictFirstOptions = (conflicts) =>
  conflicts?.map((conflict) => ({ ...conflict, selectedOptionIndex: 0 }))
